/**
 * Help / explainer videos — expandable catalog for any site area.
 *
 * To add videos for another module:
 * 1. Add an area object to HELP_VIDEO_AREAS below
 * 2. Set permission (null = all signed-in users) to match who should see it
 * 3. Set either src (local public path) or youtubeId when a recording is ready
 *
 * src: public path under /public, e.g. '/help-videos/large-gp-children-workers.mp4'
 * youtubeId: the 11-character ID from https://www.youtube.com/watch?v=XXXXXXXXXXX
 *            or https://youtu.be/XXXXXXXXXXX
 * Prefer src when hosting the file in this app; leave the other field ''.
 */
import { PERMISSIONS } from './permissions';
import { fieldGroups, fieldLabels } from '../fieldConfig';

/** Build a short field list string for a section (e.g. "Large Group Church, Children, …") */
const fieldsForSection = (sectionKey) => {
  const keys = fieldGroups[sectionKey] || [];
  return keys.map((k) => fieldLabels[k] ?? k).join(', ');
};

/**
 * @typedef {object} HelpVideo
 * @property {string} id - Stable unique id within the area
 * @property {string} title - Display title
 * @property {string} [sectionKey] - Optional link to a fieldGroups key (dynamic-entry)
 * @property {string} youtubeId - YouTube video id, or '' if not published yet
 * @property {string} [src] - Optional local public path to an mp4 (e.g. '/help-videos/….mp4')
 * @property {string} [duration] - Optional display duration hint
 * @property {string} summary - One-sentence description shown under the title
 * @property {string[]} [steps] - Optional short how-to bullets (shown when no video yet, or as captions)
 */

/**
 * @typedef {object} HelpVideoArea
 * @property {string} id - URL slug / deep-link key (e.g. "dynamic-entry")
 * @property {string} title - Area display name
 * @property {string} description - Short blurb for the hub card
 * @property {string|null} relatedPath - App path this area teaches (optional)
 * @property {string|null} permission - Required permission, or null for all signed-in users
 * @property {HelpVideo[]} videos - Ordered list of explainer videos
 */

/** @type {HelpVideoArea[]} */
export const HELP_VIDEO_AREAS = [
  {
    id: 'dynamic-entry',
    title: 'Add Group Data',
    description:
      'Short videos on how to pick the date for the night, choose your section, enter numbers, and submit.',
    relatedPath: '/dynamic-entry',
    permission: PERMISSIONS.GROUP_DATA,
    videos: [
      {
        id: 'overview',
        title: 'Overview: How to submit group data',
        youtubeId: '',
        duration: '~2 min',
        summary:
          'Walk through the full Add Group Data flow—from choosing the date to submitting for Ministry Lead review.',
        steps: [
          'Open Add Group Data from Home or the floating button.',
          'Select the date for the night you are reporting.',
          'Choose one section from the dropdown (only one section per submission).',
          'Enter the numbers for that section. If data already exists, submitting replaces it.',
          'Optionally add a comment for the Ministry Lead, then click Submit.',
        ],
      },
      ...Object.keys(fieldGroups).map((sectionKey) => {
        const fields = fieldsForSection(sectionKey);
        const slug = sectionKey
          .toLowerCase()
          .replace(/['’]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
        return {
          id: slug,
          title: sectionKey,
          sectionKey,
          youtubeId: '',
          src: `/help-videos/${slug}.mp4`,
          duration: '~20 sec',
          summary: `How to enter numbers for ${sectionKey}: ${fields}.`,
          steps: [
            `Choose section “${sectionKey}” in the dropdown.`,
            `Enter values for: ${fields}.`,
            'Add an optional comment if something notable happened in this section.',
            'Click Submit. The Ministry Lead will review and combine all sections.',
          ],
        };
      }),
    ],
  },
  {
    id: 'review',
    title: 'Ministry Lead Review',
    description:
      'How to review submitted group data, fill leftover fields, and prepare the final report.',
    relatedPath: '/review',
    permission: PERMISSIONS.REVIEW,
    videos: [
      {
        id: 'ministry-lead-review',
        title: 'Review and save a night report',
        youtubeId: '',
        src: '/help-videos/ministry-lead-review.mp4',
        duration: '~36 sec',
        summary:
          'Pick the date, review group numbers that came in, fill leftover fields, save to Pending, then hover Submit Final without sending.',
        steps: [
          'Select the date for the night (groups have often already submitted).',
          'Scroll and review every line.',
          'Fill any leftover empty fields.',
          'Save to Pending. Submit Final only when the report is ready.',
        ],
      },
    ],
  },
  {
    id: 'step-study',
    title: 'Step Study',
    description:
      'How to create a cohort, print a roster, mark attendance, and add a weekly entry.',
    relatedPath: '/step-study',
    permission: PERMISSIONS.STEP_STUDY,
    videos: [
      {
        id: 'step-study-create-cohort',
        title: 'Create a cohort',
        youtubeId: '',
        src: '/help-videos/step-study-create-cohort.mp4',
        duration: '~38 sec',
        summary: 'Open Step Study, start a new cohort, and save leaders plus a small roster.',
        steps: [
          'Open Step Study and choose New Cohort.',
          'Enter cohort details, leaders, and meeting time.',
          'Add roster names, then save the cohort.',
        ],
      },
      {
        id: 'step-study-print-roster',
        title: 'Print a weekly roster',
        youtubeId: '',
        src: '/help-videos/step-study-print-roster.mp4',
        duration: '~16 sec',
        summary: 'From inside a cohort, pick the meeting date and print the roster to fill out.',
        steps: [
          'Open the cohort and go to Attendance.',
          'Select the meeting date.',
          'Choose Print Roster, then cancel or print as needed.',
        ],
      },
      {
        id: 'step-study-save-attendance',
        title: 'Save attendance',
        youtubeId: '',
        src: '/help-videos/step-study-save-attendance.mp4',
        duration: '~28 sec',
        summary: 'Mark who is present for the week, then save attendance.',
        steps: [
          'Open the cohort on the Attendance tab.',
          'Mark each person Present or not.',
          'Click Save Attendance.',
        ],
      },
      {
        id: 'step-study-weekly-entry',
        title: 'Add Weekly Entry',
        youtubeId: '',
        src: '/help-videos/step-study-weekly-entry.mp4',
        duration: '~25 sec',
        summary: 'Enter active, dropped, new, and lesson numbers for the week, then save.',
        steps: [
          'Open Add Weekly Entry for the cohort.',
          'Pick the week date.',
          'Fill active, dropped, new, and lesson.',
          'Save the weekly entry.',
        ],
      },
    ],
  },
  {
    id: 'inventory',
    title: 'Inventory',
    description: 'How to submit monthly chip, coin, and book counts.',
    relatedPath: '/inventory',
    permission: PERMISSIONS.INVENTORY,
    videos: [
      {
        id: 'submit-inventory',
        title: 'Submit Inventory',
        youtubeId: '',
        src: '/help-videos/submit-inventory.mp4',
        duration: '~53 sec',
        summary:
          'Pick the report date, enter a few counts across tabs, then hover Submit when ready (submitting emails the report).',
        steps: [
          'Select the report date (usually the last day of the month).',
          'Enter a few chip counts on Plastic Chips.',
          'Switch tabs and enter a few more counts.',
          'Submit Inventory Report only when the form is ready.',
        ],
      },
    ],
  },
];

/** Look up an area by id */
export const getHelpVideoArea = (areaId) =>
  HELP_VIDEO_AREAS.find((a) => a.id === areaId) || null;

/** Videos for a dynamic-entry section key (excluding overview) */
export const getVideosForSection = (sectionKey) => {
  const area = getHelpVideoArea('dynamic-entry');
  if (!area || !sectionKey) return [];
  return area.videos.filter((v) => v.sectionKey === sectionKey);
};

/** Whether a video has a playable local src or YouTube id */
export const hasPlayableVideo = (video) =>
  Boolean(
    (video?.src && String(video.src).trim()) ||
      (video?.youtubeId && String(video.youtubeId).trim())
  );
